import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { TokenStorageService } from './token-storage.service';

export interface GroupMember {
  // Public business data: every member of the network sees it.
  company_id: string;
  company_name: string | null;
  company_website: string | null;
  company_address: string | null;
  company_logo_image_path: string | null;
  company_registered_at: string | null;
  role: 'member';
  status: 'pending' | 'active' | 'rejected' | 'removed';
  joined_at: string;
  // Only for the network's Zentrale; null when a member looks at its network.
  company_email: string | null;
  /** Finished onboarding (IBAN included) and can be approved. */
  onboarding_complete: boolean | null;
  has_bank_account: boolean | null;
}

export interface InviteLink {
  public_id: string;
  token: string;
  created_at: string;
  expires_at: string;
  is_active: boolean;
}

export interface CompanyGroup {
  /** public_id == central_company_id — the central company IS the group */
  public_id: string;
  name: string;
  central_company_id: string;
  central_company_name: string | null;
  central_company_address: string | null;
  central_company_website: string | null;
  central_company_email: string | null;
  central_company_phone: string | null;
  central_company_logo_image_path: string | null;
  is_active: boolean;
  created_at: string;
  members: GroupMember[];
  invite_links: InviteLink[];
}

export interface PublicCompanyGroup {
  public_id: string;
  name: string;
  central_company_name: string | null;
  member_count: number;
}

export interface GroupTransferRecord {
  redemption_id: number;
  redeemed_at: string;
  voucher_code: string;
  redeemed_amount: number;
  balance_before: number | null;
  balance_after: number | null;
  payout_cents: number; // what the member is owed under the network's settlement rule
  member_company_id: string | null;
  member_company_name: string | null;
  settlement_period?: string | null;
  settlement_status?: SettlementStatus | null;
  // Voucher type, mirroring the badges in the Gutscheine list
  voucher_product_id?: string | null;
  voucher_product_name?: string | null;
  is_internal?: boolean;
}

export interface MemberRedemptionSummary {
  member_company_id: string;
  member_company_name: string | null;
  total_redeemed_cents: number;
  redemption_count: number;
}

export interface GroupTransferOverview {
  group_public_id: string;
  group_name: string;
  transfers: GroupTransferRecord[];
  total_payout_cents: number;
  total_redeemed_cents: number;
  member_summaries: MemberRedemptionSummary[];
}

export interface MemberRedemptionRecord {
  redemption_id: number;
  redeemed_at: string;
  voucher_code: string;
  amount: number;
  balance_before: number | null;
  balance_after: number | null;
  payout_cents: number | null; // members only
  settlement_period?: string | null;
  settlement_status?: SettlementStatus | null;
  // Voucher type, mirroring the badges in the Gutscheine list
  voucher_product_id?: string | null;
  voucher_product_name?: string | null;
  is_internal?: boolean;
}

export interface MemberRedemptionStats {
  total_redeemed_cents: number;
  redemption_count: number;
  redemptions: MemberRedemptionRecord[];
}

export type SettlementStatus = 'draft' | 'exported' | 'paid' | 'cancelled';
export type SammelueberweisungFormat = 'pain.001.001.09' | 'pain.001.001.03' | 'csv';

export interface CountAndValue {
  count: number;
  value_cents: number;
}

export interface MonthlyReport {
  period: string;
  period_label: string;
  is_complete: boolean;
  network_name: string | null;
  sales: {
    count: number;
    face_value_cents: number;
    paid_cents: number;
    fees_cents: number;
    net_cents: number;
  };
  internal_issued: CountAndValue;
  refunds: CountAndValue;
  redeemed_by_members: {
    member_company_id: string;
    member_name: string | null;
    count: number;
    face_value_cents: number;
    payout_cents: number;
    has_bank_account: boolean;
    bank_iban_changed_at: string | null;
  }[];
  member_redemptions_total: CountAndValue;
  member_payout_total_cents: number;
  outstanding_paid_cents: number;
  outstanding_internal_cents: number;
}

export interface SettlementLine {
  member_company_id: string;
  member_name: string | null;
  iban_masked: string | null;
  account_holder: string;
  iban_changed_at: string | null;
  redemption_count: number;
  face_value_cents: number;
  payout_cents: number;
}

export interface Settlement {
  public_id: string;
  period: string;
  period_label: string;
  status: SettlementStatus;
  created_automatically: boolean;
  redemption_count: number;
  total_payout_cents: number;
  created_at: string;
  exported_at: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  lines: SettlementLine[];
  skipped_members: string[];
}

export interface MemberSettlement {
  period: string;
  period_label: string;
  status: SettlementStatus;
  redemption_count: number;
  face_value_cents: number;
  payout_cents: number;
  iban_masked: string | null;
  paid_at: string | null;
}

/** A file from the API plus the name the server suggests for it. */
export interface Download {
  blob: Blob;
  filename: string;
}

export interface RedemptionLocation {
  /** Unique in the list; company_id repeats for a shop's own further locations. */
  id: string;
  company_id: string;
  company_name: string;
  street: string | null;
  zip: string | null;
  city: string | null;
  has_pin: boolean;
  logo_image_path: string | null;
  latitude: number | null;
  longitude: number | null;
  website: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CompanyGroupService {
  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private tokenStorage: TokenStorageService,
  ) {}

  /** Activate (or return) the group for the logged-in central company. No body needed. */
  activateGroup(): Observable<CompanyGroup> {
    return this.http.post<CompanyGroup>(`${this.apiUrl}/company-groups`, {});
  }

  listGroups(): Observable<CompanyGroup[]> {
    return this.http.get<CompanyGroup[]>(`${this.apiUrl}/company-groups`);
  }

  getGroup(publicId: string): Observable<CompanyGroup> {
    return this.http.get<CompanyGroup>(`${this.apiUrl}/company-groups/${publicId}`);
  }

  /** E-mails the member registration link; same answer for every address. */
  inviteMemberByEmail(groupPublicId: string, email: string): Observable<{ detail: string }> {
    return this.http.post<{ detail: string }>(
      `${this.apiUrl}/company-groups/${groupPublicId}/invite`,
      { email },
    );
  }

  approveMember(groupPublicId: string, memberCompanyId: string): Observable<GroupMember> {
    return this.http.post<GroupMember>(
      `${this.apiUrl}/company-groups/${groupPublicId}/members/${memberCompanyId}/approve`,
      {},
    );
  }

  rejectMember(groupPublicId: string, memberCompanyId: string): Observable<{ detail: string }> {
    return this.http.post<{ detail: string }>(
      `${this.apiUrl}/company-groups/${groupPublicId}/members/${memberCompanyId}/reject`,
      {},
    );
  }

  createInviteLink(groupPublicId: string): Observable<InviteLink> {
    return this.http.post<InviteLink>(
      `${this.apiUrl}/company-groups/${groupPublicId}/invite-links`,
      {},
    );
  }

  revokeInviteLink(groupPublicId: string, linkPublicId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.apiUrl}/company-groups/${groupPublicId}/invite-links/${linkPublicId}`,
    );
  }

  deleteGroup(publicId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/company-groups/${publicId}`);
  }

  getTransferOverview(
    publicId: string,
    fromDate?: string,
    toDate?: string,
  ): Observable<GroupTransferOverview> {
    let params = new HttpParams();
    if (fromDate) params = params.set('from_date', `${fromDate}T00:00:00Z`);
    if (toDate) params = params.set('to_date', `${toDate}T23:59:59Z`);
    return this.http.get<GroupTransferOverview>(
      `${this.apiUrl}/company-groups/${publicId}/transfers`,
      { params },
    );
  }

  getMemberRedemptionStats(fromDate?: string, toDate?: string): Observable<MemberRedemptionStats> {
    let params = new HttpParams();
    if (fromDate) params = params.set('from_date', `${fromDate}T00:00:00Z`);
    if (toDate) params = params.set('to_date', `${toDate}T23:59:59Z`);
    return this.http.get<MemberRedemptionStats>(`${this.apiUrl}/member/redemption-stats`, {
      params,
    });
  }

  /** Get all redemptions of the logged-in company's own vouchers. */
  getCompanyRedemptions(fromDate?: string, toDate?: string): Observable<MemberRedemptionStats> {
    let params = new HttpParams();
    if (fromDate) params = params.set('from_date', `${fromDate}T00:00:00Z`);
    if (toDate) params = params.set('to_date', `${toDate}T23:59:59Z`);
    return this.http.get<MemberRedemptionStats>(`${this.apiUrl}/company/redemptions`, {
      params,
    });
  }

  getMonthlyReport(groupPublicId: string, period: string): Observable<MonthlyReport> {
    return this.http.get<MonthlyReport>(
      `${this.apiUrl}/company-groups/${groupPublicId}/reports/monthly`,
      { params: { period } },
    );
  }

  downloadMonthlyReport(
    groupPublicId: string,
    period: string,
    format: 'pdf' | 'csv',
  ): Observable<Download> {
    return this.download(`${this.apiUrl}/company-groups/${groupPublicId}/reports/monthly`, {
      period,
      format,
    });
  }

  listSettlements(groupPublicId: string): Observable<Settlement[]> {
    return this.http.get<Settlement[]>(
      `${this.apiUrl}/company-groups/${groupPublicId}/settlements`,
    );
  }

  createSettlement(groupPublicId: string, period: string): Observable<Settlement> {
    return this.http.post<Settlement>(
      `${this.apiUrl}/company-groups/${groupPublicId}/settlements`,
      { period },
    );
  }

  exportSettlement(
    groupPublicId: string,
    settlementId: string,
    format: SammelueberweisungFormat,
    executionDate: string,
  ): Observable<Download> {
    return this.download(
      `${this.apiUrl}/company-groups/${groupPublicId}/settlements/${settlementId}/export`,
      { format, execution_date: executionDate },
    );
  }

  markSettlementPaid(groupPublicId: string, settlementId: string): Observable<Settlement> {
    return this.http.post<Settlement>(
      `${this.apiUrl}/company-groups/${groupPublicId}/settlements/${settlementId}/paid`,
      {},
    );
  }

  /** After a download, the backend needs `notTransferred`: the file never went to the bank. */
  cancelSettlement(
    groupPublicId: string,
    settlementId: string,
    notTransferred = false,
  ): Observable<Settlement> {
    return this.http.post<Settlement>(
      `${this.apiUrl}/company-groups/${groupPublicId}/settlements/${settlementId}/cancel`,
      { not_transferred: notTransferred },
    );
  }

  /** A member's settlements from its network. */
  getMySettlements(): Observable<MemberSettlement[]> {
    return this.http.get<MemberSettlement[]>(`${this.apiUrl}/company/settlements`);
  }

  private download(url: string, params: Record<string, string>): Observable<Download> {
    return this.http.get(url, { params, responseType: 'blob', observe: 'response' }).pipe(
      map((res) => {
        const disposition = res.headers.get('Content-Disposition') ?? '';
        const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'download';
        return { blob: res.body as Blob, filename };
      }),
    );
  }

  /** Public — no auth needed. Used on the join-group page. */
  getPublicGroupInfo(inviteToken: string): Observable<PublicCompanyGroup> {
    return this.http.get<PublicCompanyGroup>(`${this.apiUrl}/public/company-groups/${inviteToken}`);
  }

  /** Public — get all active member locations for a company (for customer-facing pages). */
  getRedemptionLocations(companyId: string): Observable<RedemptionLocation[]> {
    return this.http.get<RedemptionLocation[]>(
      `${this.apiUrl}/public/redemption-locations/${companyId}`,
    );
  }

  /** Shareable member registration link; one link serves any number of members. */
  buildInviteUrl(inviteToken: string): string {
    return `${environment.frontendUrl}/register?verbund_invite=${inviteToken}`;
  }
}
